import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { ChevronRight, Calendar } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getNews } from "@/lib/api";
import { formatDateLong } from "@/lib/dateUtils";

const formatDate = (dateStr) => formatDateLong(dateStr);

export default function News() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await getNews({ limit: 20 });
        setNews(response.data);
      } catch (error) {
        console.error("Error fetching news:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="animate-pulse">
          <div className={`h-10 w-32 ${isDark ? 'bg-slate-800' : 'bg-slate-200'} rounded mb-8`} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={`h-80 ${isDark ? 'bg-slate-800' : 'bg-slate-200'} rounded-lg`} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="news-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl md:text-4xl font-black font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>NIEUWS</h1>
          <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Laatste updates van Futsal Gent</p>
        </div>

        {/* Featured Article */}
        {news.length > 0 && news[0].is_featured && (
          <Card className={`overflow-hidden mb-8 card-hover ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`} data-testid="featured-article">
            <div className="grid md:grid-cols-2">
              {news[0].image_url && (
                <div className="h-64 md:h-auto overflow-hidden">
                  <img 
                    src={news[0].image_url} 
                    alt={news[0].title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <CardContent className="p-6 md:p-8 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <Badge className="badge-gold">Uitgelicht</Badge>
                  <Badge variant="outline" className={isDark ? 'border-white/20 text-slate-400' : 'border-slate-300 text-slate-600'}>
                    {news[0].category}
                  </Badge>
                </div>
                <h2 className={`text-2xl md:text-3xl font-bold mb-4 font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {news[0].title}
                </h2>
                <p className={`mb-4 line-clamp-3 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{news[0].summary}</p>
                <div className="flex items-center justify-between">
                  <span className={`text-sm flex items-center gap-2 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                    <Calendar className="h-4 w-4" />
                    {formatDate(news[0].published_at)}
                  </span>
                  <Link 
                    to={`/news/${news[0].id}`}
                    className="inline-flex items-center text-[hsl(217,91%,60%)] hover:text-[hsl(48,96%,53%)] transition-colors font-medium"
                    data-testid="featured-read-more"
                  >
                    Lees Meer <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </div>
              </CardContent>
            </div>
          </Card>
        )}

        {/* News Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {news.slice(news[0]?.is_featured ? 1 : 0).map((article, index) => (
            <Card 
              key={article.id}
              className={`overflow-hidden card-hover news-card ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}
              data-testid={`news-card-${index}`}
            >
              {article.image_url && (
                <div className="h-48 overflow-hidden">
                  <img 
                    src={article.image_url} 
                    alt={article.title}
                    className="w-full h-full object-cover news-image"
                  />
                </div>
              )}
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className={isDark ? 'border-white/20 text-slate-400' : 'border-slate-300 text-slate-600'}>
                    {article.category}
                  </Badge>
                  <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                    <Calendar className="h-3 w-3" />
                    {formatDate(article.published_at)}
                  </span>
                </div>
                <h3 className={`font-bold mb-2 line-clamp-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>{article.title}</h3>
                <p className={`text-sm line-clamp-3 mb-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{article.summary}</p>
                <Link 
                  to={`/news/${article.id}`}
                  className="inline-flex items-center text-[hsl(217,91%,60%)] text-sm hover:text-[hsl(48,96%,53%)] transition-colors"
                  data-testid={`news-read-more-${index}`}
                >
                  Lees Meer <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        {news.length === 0 && (
          <div className="text-center py-12">
            <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>Geen nieuwsartikelen beschikbaar</p>
          </div>
        )}
      </div>
    </div>
  );
}
